import { Alert, Box, Button, Card, CardContent, Container, Grid, Stack, TextField, Typography, InputAdornment } from '@mui/material';
import { Login as LoginIcon, Person, Lock } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../../features/auth/queries';
import { useAuthStore } from '../../features/auth/store';
import { extractError } from '../../shared/api';
import Logo from '../../shared/Logo';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const login = useLogin();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (user) navigate(user.mustResetPassword ? '/portal/change-password' : '/portal');
  }, [user, navigate]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ username: username.trim(), password });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1E3A8A 0%, #172554 100%)',
      display: 'flex', alignItems: 'center', py: 4,
    }}>
      <Container maxWidth="md">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6} sx={{ color: '#fff', display: { xs: 'none', md: 'block' } }}>
            <Logo variant="inverse" size={56} />
            <Typography variant="h3" sx={{ mt: 4, fontWeight: 800, letterSpacing: '-0.02em' }}>
              Baseras School Portal
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, opacity: 0.85, fontWeight: 400 }}>
              Where Curiosity Meets Excellence.
            </Typography>
            <Typography variant="body2" sx={{ mt: 4, opacity: 0.75 }}>
              One sign-in for students, teachers, administrators and the principal.
              Notices, marks, attendance, fees and timetables — all in one place.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                  <Logo />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Sign in</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Use your roll number (students) or username (staff).
                </Typography>

                <Box component="form" onSubmit={onSubmit} sx={{ mt: 3 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Roll Number / Username" autoFocus
                      value={username} onChange={(e) => setUsername(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment> }}
                      required
                    />
                    <TextField
                      label="Password" type="password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Lock fontSize="small" /></InputAdornment> }}
                      required
                    />
                    {login.isError && <Alert severity="error">{extractError(login.error)}</Alert>}
                    <Button
                      type="submit" variant="contained" size="large"
                      startIcon={<LoginIcon />} disabled={login.isPending}
                    >
                      {login.isPending ? 'Signing in…' : 'Sign in'}
                    </Button>
                    <Button onClick={() => navigate('/')} size="small" sx={{ alignSelf: 'flex-end' }}>
                      ← Back to website
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
